import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  ArrowIcon,
  Container,
  DropdownWrapper,
  Options,
  Selector,
  Option,
  Label,
  YearInput
} from "./dateSelector.styles";
import { useTranslation } from "react-i18next";
import { onlyNumbers } from "utils/helpers";

interface DateSelectorProps {
  initialDay: number | null;
  initialMonth: number | null;
  initialYear: number | null;
  onChange?: (day: number | null, month: number | null, year: number | null) => void;
  separate?: boolean;
  showLabels?: boolean;
}

const getDaysInMonth = (year: number, month: number) => {
  return new Date(year, month, 0).getDate();
};

const DateSelector: React.FC<DateSelectorProps> = ({
                                                     initialDay,
                                                     initialMonth,
                                                     initialYear,
                                                     onChange,
                                                     separate,
                                                     showLabels
                                                   }) => {
  const {t} = useTranslation();
  const today = new Date();

  const [year, setYear] = useState<number | null>(initialYear);
  const [month, setMonth] = useState<number | null>(initialMonth);
  const [day, setDay] = useState<number | null>(initialDay);
  const [yearInput, setYearInput] = useState<string>(initialYear?.toString() ?? '');
  const [dayInput, setDayInput] = useState<string>(initialDay?.toString() ?? '');

  const [openDropdown, setOpenDropdown] = useState<'day' | 'month' | 'year' | null>(null);

  // Добавляем рефы для инпутов
  const dayInputRef = useRef<HTMLInputElement>(null);
  const yearInputRef = useRef<HTMLInputElement>(null);

  const locale = localStorage.getItem('i18next') || 'en-US';

  const monthNames = useMemo(() => {
    const names = Array.from({length: 12}, (_, i) => {
      const date = new Date(2020, i, 1);
      return {
        value: i + 1,
        label: new Intl.DateTimeFormat(locale, {month: 'short'}).format(date),
      };
    });
    return [{value: null, label: '-'}, ...names];
  }, [locale]);

  // Calculate maximum days based on selected month and year.
  const maxDays = month && year ? getDaysInMonth(year, month) : 31;

  // Ensure the selected day is valid if month/year change.
  useEffect(() => {
    if (day && month && year && day > maxDays) {
      setDay(null);
      onChange && onChange(null, month, year);
    }
  }, [month, year, day, maxDays, onChange]);

  // Generate day options including a clear option.
  const dayOptions = [null, ...Array.from({length: maxDays}, (_, i) => i + 1)];
  useEffect(() => {
    setDayInput(day?.toString() ?? '');
  }, [day]);

  const commitDay = (raw: string) => {
    const trimmed = raw.trim();
    if (trimmed === '') {
      setDay(null);
      onChange && onChange(null, month, year);
      return;
    }
    const parsed = Number(trimmed);
    if (Number.isNaN(parsed)) return;
    const clamped = Math.max(1, Math.min(maxDays, parsed));
    setDay(clamped);
    onChange && onChange(clamped, month, year);
  };

  // Year options with a clear option.
  const currentYear = today.getFullYear();
  const yearOptions = [null, ...Array.from({length: 1000}, (_, i) => currentYear - i)];

  useEffect(() => {
    setYearInput(year?.toString() ?? '');
  }, [year]);

  const commitYear = (raw: string) => {
    const trimmed = raw.trim();
    if (trimmed === '') {
      setYear(null);
      onChange && onChange(day, month, null);
      return;
    }

    const parsed = Number(trimmed);
    if (Number.isNaN(parsed)) return;

    const clamped = Math.max(0, Math.min(currentYear, parsed));
    setYear(clamped);

    if (day && month && clamped && day > getDaysInMonth(clamped, month)) {
      setDay(null);
    }
    onChange && onChange(day, month, clamped);
  };

  // Toggle dropdown and focus input
  const toggleDropdown = (type: 'day' | 'month' | 'year') => {
    setOpenDropdown(prev => {
      // Если открываем дропдаун, фокусируемся на соответствующем инпуте
      if (prev !== type) {
        if (type === 'day' && dayInputRef.current) {
          dayInputRef.current.focus();
        } else if (type === 'year' && yearInputRef.current) {
          yearInputRef.current.focus();
        }
      }
      return prev === type ? null : type;
    });
  };

  // Handle selection; if value is undefined, deselect.
  const handleSelect = (type: 'day' | 'month' | 'year', value: number | null) => {
    if (type === 'day') {
      setDay(value);
      onChange && onChange(value, month, year);
    } else if (type === 'month') {
      setMonth(value);
      // Clear day if new month makes previous day invalid.
      if (day && value && year && day > getDaysInMonth(year, value)) {
        setDay(null);
      }
      onChange && onChange(day, value, year);
    } else {
      setYear(value);
      // Clear day if new year makes previous day invalid.
      if (day && month && value && day > getDaysInMonth(value, month)) {
        setDay(null);
      }
      onChange && onChange(day, month, value);
    }
    setOpenDropdown(null);
  };

  // Close dropdown on outside click.
  const wrapperRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Helper to render display value with placeholder.
  const renderDisplay = (value: number | null, placeholder: string, list?: {
    value: number | null;
    label: string
  }[]) => {
    if (!value) return placeholder;
    if (list) {
      const found = list.find(item => item.value === value);
      return found ? found.label : value;
    }
    return value;
  };

  const handleKeyDown = (e: React.KeyboardEvent, type: 'day' | 'year') => {
    if (e.key === 'Enter') {
      if (type === 'day') {
        commitDay(dayInput);
      } else {
        commitYear(yearInput);
      }
      setOpenDropdown(null);
    }
  };

  return (
    <Container separate={separate} ref={wrapperRef}>
      {/* Day Selector */}
      <DropdownWrapper separate={separate}>
        {showLabels && <Label>{t('wall.day')}</Label>}

        <Selector
          style={{width: '54px', position: 'relative'}}
          separate={separate}
          onClick={() => toggleDropdown('day')}
        >
          <YearInput
            ref={dayInputRef}
            type="text"
            min={1}
            max={maxDays}
            value={dayInput || "-"}
            onChange={(e) => setDayInput(onlyNumbers(e.target.value, "", 1, maxDays))}
            onBlur={() => commitDay(dayInput)}
            onFocus={() => setOpenDropdown('day')}
            onKeyDown={(e) => handleKeyDown(e, 'day')}
            onClick={(e) => e.stopPropagation()}
          />
          <ArrowIcon
            open={openDropdown === 'day'}
            onClick={(e) => {
              e.stopPropagation();
              toggleDropdown('day');
            }}
            style={{pointerEvents: 'auto'}}
          />
        </Selector>

        {openDropdown === 'day' && (
          <Options>
            {dayOptions.map((d, idx) => (
              <Option key={idx} onClick={() => handleSelect('day', d)}>
                {d === null ? '-' : d}
              </Option>
            ))}
          </Options>
        )}
      </DropdownWrapper>

      {/* Month Selector */}
      <DropdownWrapper separate={separate}>
        {showLabels && (
          <Label>{t('wall.month')}</Label>
        )}
        <Selector style={{width: '68px'}} separate={separate} onClick={() => toggleDropdown('month')}>
          {renderDisplay(month, showLabels ? '-' : t('wall.month'), monthNames)}
          <ArrowIcon open={openDropdown === 'month'}/>
        </Selector>
        {openDropdown === 'month' && (
          <Options>
            {monthNames.map((m, idx) => (
              <Option key={idx} onClick={() => handleSelect('month', m.value)}>
                {m.label}
              </Option>
            ))}
          </Options>
        )}
      </DropdownWrapper>

      {/* Year Selector */}
      <DropdownWrapper separate={separate}>
        {showLabels && <Label>{t('wall.year')}</Label>}

        <Selector
          style={{width: '64px', position: 'relative'}}
          separate={separate}
          onClick={() => toggleDropdown('year')}
        >
          <YearInput
            ref={yearInputRef}
            type="text"
            min={0}
            max={currentYear}
            value={yearInput || "-"}
            onChange={(e) => setYearInput(onlyNumbers(e.target.value, "", 0, currentYear))}
            onBlur={() => commitYear(yearInput)}
            onFocus={() => setOpenDropdown('year')}
            onKeyDown={(e) => handleKeyDown(e, 'year')}
            onClick={(e) => e.stopPropagation()}
          />
          <ArrowIcon
            open={openDropdown === 'year'}
            onClick={(e) => {
              e.stopPropagation();
              toggleDropdown('year');
            }}
            style={{pointerEvents: 'auto'}}
          />
        </Selector>

        {openDropdown === 'year' && (
          <Options>
            {yearOptions.map((y, idx) => (
              <Option key={idx} onClick={() => handleSelect('year', y)}>
                {y === null ? '-' : y}
              </Option>
            ))}
          </Options>
        )}
      </DropdownWrapper>
    </Container>
  );
};

export default DateSelector;